import React, {Component} from 'react';
import {NotificationManager} from 'react-notifications';
import {Route} from 'react-router';
import {Link} from 'react-router-dom';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';
import withUrlState from '../../lib/with-url-state';
import ListScreen from '../../components/ListScreen';
import Tabs from '../../components/Tabs';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import ListItem from '../../components/ListItem';
import CanEditGroup from '../../security/rules/CanEditGroup';

import GroupFormModal from './GroupFormModal';
import CanCreateGroup from "../../security/rules/CanCreateGroup";
import search from '../../lib/search';
import {alphaSort} from '../../lib/sort';
import {T} from '../../components/I18n';

class UserGroupTab extends Component {
  state = {
    groups: [],
    loading: true,
    failed: false
  };

  async componentDidMount() {
    const {credentials} = this.props;

    try {
      const [groups, users] = await Promise.all([api.getUserGroups(credentials), api.getAllUsers(credentials)]);
      this.setState({groups, users});
    }
    catch(e) {
      this.setState({failed: true});
    }
    finally {
      this.setState({loading: false});
    }
  }

  handleSearch = e => {
    this.props.setUrlState({filter: e.target.value});
  }

  handleSort = e => {
    this.props.setUrlState({sort: e.target.value});
  }

  handleDeleteGroup = async group => {
    const {credentials} = this.props;

    // eslint-disable-next-line
    if(!confirm(`Are you sure you want to delete this group?\n\nForms assigned to this group may become visible to all users.`)) {
      return;
    }

    this.setState(state => ({groups: state.groups.filter(g => g.id !== group.id)}));

    try {
      await api.deleteUserGroup(group, credentials);
      NotificationManager.success(`${group.name} was deleted`);
    }
    catch(e) {
      NotificationManager.error('Unable to delete user group. Please try again');
    }
  }

  handleCloseModal = () => {
    const {history, location: {search: queryString}} = this.props;
    history.push(`/users/groups${queryString}`);
  }

  handleGroupSaved = group => {
    this.setState(prevState => ({
      groups: prevState.groups.filter(g => g.id !== group.id).concat([group])
    }), this.handleCloseModal);
  }

  renderGroupModal = ({match: {params: {id}}}) => {
    const {groups, users} = this.state;

    if (id === 'create') {
      return (
        <GroupFormModal
          open={true}
          users={users}
          onClose={this.handleCloseModal}
          onSave={this.handleGroupSaved}
        />
      );
    }

    let group = null;

    if (id) {
      group = groups.filter(group => group.id === id)[0];
    }

    return (
      <GroupFormModal
        open={!!group}
        group={group}
        users={users}
        onClose={this.handleCloseModal}
        onSave={this.handleGroupSaved}
      />
    );
  }

  render() {
    const {urlState: {sort, filter}, location: {search: queryString}, user} = this.props;
    const {loading, error, groups} = this.state;
    const filteredAndSortedGroups = groups
      .filter(group => search(group.name, filter))
      .sort(alphaSort(sort, group => group.name));
    
    return (
      <>
        <ListScreen
          title="User Groups"
          activeLink="Users"
          loading={loading && <Loading fullScreen />}
          error={error && <Error fullScreen />}
          empty={<>There are no user groups to display.{ CanCreateGroup(user)  &&
          <> Get started by <Link to={`/users/groups/create${queryString}`}>creating a new user group.</Link></>}</>}
          items={filteredAndSortedGroups}
          search={filter}
          onSearch={this.handleSearch}
          primaryAction={CanCreateGroup(user) && <Button to={`/users/groups/create${queryString}`}>New User Group</Button>}
          toolbarActions={[
            <Input id="group-sort" label="Sort by:" type="select" value={sort} onChange={this.handleSort}>
              <option value="asc">Alphabetical</option>
              <option value="desc">Reverse Alphabetical</option>
            </Input>
          ]}
          tabs={
            <Tabs>
              <Tabs.Tab to="/users">Users</Tabs.Tab>
              <Tabs.Tab active to="/users/groups">User Groups</Tabs.Tab>
            </Tabs>
          }>
          {(group, i) => (
            <ListItem
              first={i === 0}
              title={<T noTranslate>{group.name}</T>}
              subtitle={<T isolate><T variable="membersCount">{group.members.length}</T> members</T>}
              options={CanEditGroup(user) ? [
                {title: 'Edit', to: `/users/groups/${group.id + queryString}`},
                CanCreateGroup(user) && {title: 'Delete group', onClick: () => this.handleDeleteGroup(group)}
              ] : [{title: 'Edit', to: `/users/groups/${group.id + queryString}`}]}
            />
          )}
        </ListScreen>
        {!loading && !error && <Route path="/users/groups/:id?" component={this.renderGroupModal} />}
      </>
    );
  }
}

const defaultUrlState = {
  sort: 'asc',
  filter: ''
};

export default withUser(withUrlState(defaultUrlState)(UserGroupTab));
